// @flow

// -----------------------------------------------------------------------------------------------------------------
// Theme types
// -----------------------------------------------------------------------------------------------------------------

import { css } from 'styled-components';
import type {
  Theme,
  ThemeButton,
  ThemeColours,
  ThemeFooter,
  ThemeForm,
  ThemePlaceholder,
  ThemeTextRules,
  ThemeWidget,
} from '../theme.types';

// -----------------------------------------------------------------------------------------------------------------
// Spacing
// -----------------------------------------------------------------------------------------------------------------

/**
 * Array of possible padding/margin sizes.
 *
 * @type string[]
 */
const themeSpacing = [...Array(6).keys()].reduce(
  (spacingSizes, number) => Object.assign(spacingSizes, { [number]: `${number / 4}rem !important` }),
  {},
);

// -----------------------------------------------------------------------------------------------------------------
// Basic colours
// -----------------------------------------------------------------------------------------------------------------

// Colour definitions
// ------------------

const themeColours: ThemeColours = {
  primary: '#141760',
  primaryInverse: '#fff',
  secondary: '#73498c',
  secondaryInverse: '#fff',
  tertiary: '#cb007a',
  tertiaryInverse: '#fff',
  quaternary: '#00a651',
  quaternaryInverse: '#fff',
  subtleHighlight: '#fbad18',
  subtleHighlightInverse: '#141760',
};

// Colour CSS rules
// ----------------

const themeTextRules: ThemeTextRules = {
  primary: {
    color: themeColours.primary,
    backgroundColor: themeColours.primaryInverse,
  },
  primaryInverse: {
    color: themeColours.primaryInverse,
    backgroundColor: themeColours.primary,
  },
  secondary: {
    color: themeColours.secondary,
    backgroundColor: themeColours.secondaryInverse,
  },
  secondaryInverse: {
    color: themeColours.secondaryInverse,
    backgroundColor: themeColours.secondary,
  },
  tertiary: {
    color: themeColours.tertiary,
    backgroundColor: themeColours.tertiaryInverse,
  },
  tertiaryInverse: {
    color: themeColours.tertiaryInverse,
    backgroundColor: themeColours.tertiary,
  },
  quaternary: {
    color: themeColours.quaternary,
    backgroundColor: themeColours.quaternaryInverse,
  },
  quaternaryInverse: {
    color: themeColours.quaternaryInverse,
    backgroundColor: themeColours.quaternary,
  },
  subtleHighlight: {
    color: themeColours.subtleHighlight,
    backgroundColor: themeColours.subtleHighlightInverse,
  },
  subtleHighlightInverse: {
    color: themeColours.subtleHighlightInverse,
    backgroundColor: themeColours.subtleHighlight,
  },
};

// -----------------------------------------------------------------------------------------------------------------
// Forms
// -----------------------------------------------------------------------------------------------------------------

const themeForm: ThemeForm = {
  requiredFieldMarker: {
    '&:after': {
      color: '#f00',
      content: "'*'",
      position: 'relative',
      top: '-.2rem',
    },
  },
};

// -----------------------------------------------------------------------------------------------------------------
// Placeholders
// -----------------------------------------------------------------------------------------------------------------

const themePlaceholder: ThemePlaceholder = {
  color: themeColours.subtleHighlight,
};

// -----------------------------------------------------------------------------------------------------------------
// Widgets
// -----------------------------------------------------------------------------------------------------------------

const themeWidget: ThemeWidget = {
  widget: {
    ...themeTextRules.primary,
    marginBottom: themeSpacing[3],
    border: `1px solid ${themeColours.primary}`,
    padding: themeSpacing[0],
  },
  title: {
    ...themeTextRules.primaryInverse,
    margin: themeSpacing[0],
    fontSize: '1.2rem',
    paddingLeft: themeSpacing[3],
    paddingRight: themeSpacing[3],
    paddingTop: themeSpacing[2],
    paddingBottom: themeSpacing[2],
  },
  body: {
    ...themeTextRules.primary,
    margin: themeSpacing[0],
    padding: themeSpacing[2],
  },
};

// -----------------------------------------------------------------------------------------------------------------
// Header
// -----------------------------------------------------------------------------------------------------------------

// Header content
// --------------

const themeHeaderContent = {
  ...themeTextRules.primary,
};

// Header NavBar
// -------------

const themeHeaderNavBar = {
  ...themeTextRules.secondaryInverse,

  '.nav-link': {
    color: `${themeColours.secondaryInverse} !important`,
  },

  '.navbar-toggler': {
    color: `${themeColours.secondaryInverse} !important`,
    borderColor: themeColours.secondaryInverse,
  },
};

// -----------------------------------------------------------------------------------------------------------------
// Footer
// -----------------------------------------------------------------------------------------------------------------

const themeFooter: ThemeFooter = css`
  ${{ ...themeTextRules.secondaryInverse }};

  .heading {
    color: ${themeColours.secondaryInverse};
  }

  a {
    color: ${themeColours.secondaryInverse};
    text-decoration: underline;
  }
`;

// -----------------------------------------------------------------------------------------------------------------
// Button
// -----------------------------------------------------------------------------------------------------------------

const themeButton: ThemeButton = css`
  border-radius: 0;
`;

// -----------------------------------------------------------------------------------------------------------------
// Compile theme
// -----------------------------------------------------------------------------------------------------------------

/**
 * Theme styles object.
 *
 * @type {object}
 */
let theme2018: Theme = {
  spacing: themeSpacing,
  colors: themeColours,
  textRules: themeTextRules,
  form: themeForm,
  placeholder: themePlaceholder,
  widget: themeWidget,
  headerContent: themeHeaderContent,
  headerNavBar: themeHeaderNavBar,
  footer: themeFooter,
  button: themeButton,
};

export default theme2018;



// WEBPACK FOOTER //
// ./src/theme/themes/theme2018.js